import React, { useState, useEffect } from 'react';
import { investmentService } from '../../services/api';

function InvestmentsManagement() {
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState(null);
  const [formData, setFormData] = useState({
    investor_id: '',
    amount: '',
    type: '',
    return_rate: '',
    start_date: '',
    end_date: '',
  });

  useEffect(() => {
    loadInvestments();
  }, []);

  const loadInvestments = async () => {
    try {
      const data = await investmentService.index();
      setInvestments(data.data || []);
    } catch (error) {
      console.error('Error loading investments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingInvestment) {
        await investmentService.update(editingInvestment.id, formData);
      } else {
        await investmentService.store(formData);
      }
      setShowModal(false);
      setEditingInvestment(null);
      setFormData({ investor_id: '', amount: '', type: '', return_rate: '', start_date: '', end_date: '' });
      loadInvestments();
    } catch (error) {
      console.error('Error saving investment:', error);
    }
  };

  const handleEdit = (investment) => {
    setEditingInvestment(investment);
    setFormData({
      investor_id: investment.investor_id,
      amount: investment.amount,
      type: investment.type,
      return_rate: investment.return_rate,
      start_date: investment.start_date,
      end_date: investment.end_date,
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this investment?')) {
      try {
        await investmentService.destroy(id);
        loadInvestments();
      } catch (error) {
        console.error('Error deleting investment:', error);
      }
    }
  };

  const handleApprove = async (id) => {
    try {
      await investmentService.approve(id);
      loadInvestments();
    } catch (error) {
      console.error('Error approving investment:', error);
    }
  };

  const handleWithdraw = async (id) => {
    if (window.confirm('Are you sure you want to process this withdrawal?')) {
      try {
        await investmentService.withdraw(id);
        loadInvestments();
      } catch (error) {
        console.error('Error processing withdrawal:', error);
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      active: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      withdrawn: 'bg-gray-100 text-gray-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Investments Management</h2>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
        >
          Add Investment
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Investor ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Return Rate</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {investments.map((investment) => (
              <tr key={investment.id}>
                <td className="px-6 py-4 whitespace-nowrap">{investment.investor_id}</td>
                <td className="px-6 py-4 whitespace-nowrap">${investment.amount}</td>
                <td className="px-6 py-4 whitespace-nowrap">{investment.type}</td>
                <td className="px-6 py-4 whitespace-nowrap">{investment.return_rate}%</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(investment.status)}`}>
                    {investment.status}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap space-x-2">
                  <button
                    onClick={() => handleEdit(investment)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    Edit
                  </button>
                  {investment.status === 'pending' && (
                    <button
                      onClick={() => handleApprove(investment.id)}
                      className="text-green-600 hover:text-green-900"
                    >
                      Approve
                    </button>
                  )}
                  {investment.status === 'active' && (
                    <button
                      onClick={() => handleWithdraw(investment.id)}
                      className="text-purple-600 hover:text-purple-900"
                    >
                      Withdraw
                    </button>
                  )}
                  <button
                    onClick={() => handleDelete(investment.id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {investments.length === 0 && (
          <div className="text-center py-8 text-gray-500">No investments found</div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white p-6 rounded-lg w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingInvestment ? 'Edit Investment' : 'Add Investment'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Investor ID</label>
                <input
                  type="text"
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  value={formData.investor_id}
                  onChange={(e) => setFormData({ ...formData, investor_id: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Amount</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <select
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="">Select Type</option>
                  <option value="short_term">Short Term</option>
                  <option value="long_term">Long Term</option>
                  <option value="recurring">Recurring</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Return Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  value={formData.return_rate}
                  onChange={(e) => setFormData({ ...formData, return_rate: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Start Date</label>
                <input
                  type="date"
                  required
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">End Date</label>
                <input
                  type="date"
                  className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingInvestment(null);
                    setFormData({ investor_id: '', amount: '', type: '', return_rate: '', start_date: '', end_date: '' });
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingInvestment ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default InvestmentsManagement;
